import { HttpErrorResponse } from "@angular/common/http";
import { createAction, props } from "@ngrx/store";
import {
  ForumAttachment,
  ForumAttachmentConfig,
  ForumAttachmentCreatePayload,
  ForumMember,
  ForumOverviewGroup,
  ForumPost,
  ForumPostCreatePayload,
  ForumPostReaction,
  ForumPostUpdatePayload,
  ForumReactionActionPayload,
  ForumReactionOption,
  ForumReactionSummary,
  ForumThreadReaction,
  ForumThreadReactionDetails,
  ForumThreadRead,
  ForumThread,
  ForumThreadCreatePayload,
  ForumThreadUpdatePayload,
} from "src/app/shared/interfaces/forum";

const prefix = "[Forum]";

export const forumThreadsLoad = createAction(
  `${prefix} Threads Load`,
  props<{
    courseId: string;
    groupId: string;
    search?: string;
    archived?: boolean;
    page?: number;
  }>(),
);
export const forumThreadsLoadSuccess = createAction(
  `${prefix} Threads Load Success`,
  props<{ threads: ForumThread[]; next: number | null; page: number }>(),
);
export const forumThreadsLoadFailure = createAction(
  `${prefix} Threads Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);
export const forumThreadsReset = createAction(`${prefix} Threads Reset`);

export const forumThreadDetailsLoad = createAction(
  `${prefix} Thread Details Load`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    openEditModal?: boolean;
    members?: ForumMember[];
    attachmentConfig?: ForumAttachmentConfig | null;
  }>(),
);
export const forumThreadDetailsLoadSuccess = createAction(
  `${prefix} Thread Details Load Success`,
  props<{
    thread: ForumThread;
    editModalContext?: {
      courseId: string;
      groupId: string;
      members?: ForumMember[];
      attachmentConfig?: ForumAttachmentConfig | null;
    };
  }>(),
);
export const forumThreadDetailsLoadFailure = createAction(
  `${prefix} Thread Details Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumAttachmentConfigLoad = createAction(
  `${prefix} Attachment Config Load`,
  props<{ courseId: string; groupId: string }>(),
);
export const forumAttachmentConfigLoadSuccess = createAction(
  `${prefix} Attachment Config Load Success`,
  props<{ config: ForumAttachmentConfig }>(),
);
export const forumAttachmentConfigLoadFailure = createAction(
  `${prefix} Attachment Config Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadCreate = createAction(
  `${prefix} Thread Create`,
  props<{
    courseId: string;
    groupId: string;
    payload: ForumThreadCreatePayload;
    requestId?: string;
  }>(),
);
export const forumThreadCreateSuccess = createAction(
  `${prefix} Thread Create Success`,
  props<{ thread: ForumThread }>(),
);
export const forumThreadCreateFailure = createAction(
  `${prefix} Thread Create Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadUpdate = createAction(
  `${prefix} Thread Update`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    payload: ForumThreadUpdatePayload;
    requestId?: string;
    removedAttachmentIds?: string[];
  }>(),
);
export const forumThreadUpdateSuccess = createAction(
  `${prefix} Thread Update Success`,
  props<{ thread: ForumThread }>(),
);
export const forumThreadUpdateFailure = createAction(
  `${prefix} Thread Update Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadDelete = createAction(
  `${prefix} Thread Delete`,
  props<{ courseId: string; groupId: string; threadId: string }>(),
);
export const forumThreadDeleteSuccess = createAction(
  `${prefix} Thread Delete Success`,
  props<{ threadId: string }>(),
);
export const forumThreadDeleteFailure = createAction(
  `${prefix} Thread Delete Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadRestore = createAction(
  `${prefix} Thread Restore`,
  props<{ courseId: string; groupId: string; threadId: string }>(),
);
export const forumThreadRestoreSuccess = createAction(
  `${prefix} Thread Restore Success`,
  props<{ thread: ForumThread }>(),
);
export const forumThreadRestoreFailure = createAction(
  `${prefix} Thread Restore Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadDeletePernament = createAction(
  `${prefix} Thread Delete Pernament`,
  props<{ courseId: string; groupId: string; threadId: string }>(),
);
export const forumThreadDeletePernamentSuccess = createAction(
  `${prefix} Thread Delete Pernament Success`,
  props<{ threadId: string }>(),
);
export const forumThreadDeletePernamentFailure = createAction(
  `${prefix} Thread Delete Pernament Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadPin = createAction(
  `${prefix} Thread Pin`,
  props<{ courseId: string; groupId: string; threadId: string }>(),
);
export const forumThreadPinSuccess = createAction(
  `${prefix} Thread Pin Success`,
  props<{ thread: ForumThread }>(),
);
export const forumThreadPinFailure = createAction(
  `${prefix} Thread Pin Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadUnpin = createAction(
  `${prefix} Thread Unpin`,
  props<{ courseId: string; groupId: string; threadId: string }>(),
);
export const forumThreadUnpinSuccess = createAction(
  `${prefix} Thread Unpin Success`,
  props<{ threadId: string }>(),
);
export const forumThreadUnpinFailure = createAction(
  `${prefix} Thread Unpin Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumPostsLoad = createAction(
  `${prefix} Posts Load`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    ordering?: "created_at" | "-created_at";
    page?: number;
  }>(),
);
export const forumPostsLoadSuccess = createAction(
  `${prefix} Posts Load Success`,
  props<{
    threadId: string;
    posts: ForumPost[];
    next: number | null;
    page: number;
  }>(),
);
export const forumPostsLoadFailure = createAction(
  `${prefix} Posts Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumPostCreate = createAction(
  `${prefix} Post Create`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    payload: ForumPostCreatePayload;
    requestId?: string;
  }>(),
);
export const forumPostCreateSuccess = createAction(
  `${prefix} Post Create Success`,
  props<{
    threadId: string;
    post: ForumPost;
    pendingAttachmentsCount?: number;
  }>(),
);
export const forumPostAttachmentsUploadFinished = createAction(
  `${prefix} Post Attachments Upload Finished`,
  props<{ threadId: string; postId: string }>(),
);
export const forumPostCreateFailure = createAction(
  `${prefix} Post Create Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumPostUpdate = createAction(
  `${prefix} Post Update`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    postId: string;
    payload: ForumPostUpdatePayload;
    requestId?: string;
    removedAttachmentIds?: string[];
  }>(),
);
export const forumPostUpdateSuccess = createAction(
  `${prefix} Post Update Success`,
  props<{ threadId: string; post: ForumPost }>(),
);
export const forumPostUpdateFailure = createAction(
  `${prefix} Post Update Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumPostDelete = createAction(
  `${prefix} Post Delete`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    postId: string;
  }>(),
);
export const forumPostDeleteSuccess = createAction(
  `${prefix} Post Delete Success`,
  props<{ threadId: string; postId: string }>(),
);
export const forumPostDeleteFailure = createAction(
  `${prefix} Post Delete Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumAttachmentsLoad = createAction(
  `${prefix} Attachments Load`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    postId: string;
  }>(),
);
export const forumAttachmentsLoadSuccess = createAction(
  `${prefix} Attachments Load Success`,
  props<{ postId: string; attachments: ForumAttachment[] }>(),
);
export const forumAttachmentsLoadFailure = createAction(
  `${prefix} Attachments Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumAttachmentCreate = createAction(
  `${prefix} Attachment Create`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    postId: string;
    payload: ForumAttachmentCreatePayload;
  }>(),
);
export const forumAttachmentCreateSuccess = createAction(
  `${prefix} Attachment Create Success`,
  props<{ postId: string; attachment: ForumAttachment }>(),
);
export const forumAttachmentCreateFailure = createAction(
  `${prefix} Attachment Create Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumAttachmentDelete = createAction(
  `${prefix} Attachment Delete`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    postId: string;
    attachmentId: string;
  }>(),
);
export const forumAttachmentDeleteSuccess = createAction(
  `${prefix} Attachment Delete Success`,
  props<{ postId: string; attachmentId: string }>(),
);
export const forumAttachmentDeleteFailure = createAction(
  `${prefix} Attachment Delete Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadAttachmentsLoad = createAction(
  `${prefix} Thread Attachments Load`,
  props<{ courseId: string; groupId: string; threadId: string }>(),
);
export const forumThreadAttachmentsLoadSuccess = createAction(
  `${prefix} Thread Attachments Load Success`,
  props<{ threadId: string; attachments: ForumAttachment[] }>(),
);
export const forumThreadAttachmentsLoadFailure = createAction(
  `${prefix} Thread Attachments Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadAttachmentCreate = createAction(
  `${prefix} Thread Attachment Create`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    payload: ForumAttachmentCreatePayload;
  }>(),
);
export const forumThreadAttachmentCreateSuccess = createAction(
  `${prefix} Thread Attachment Create Success`,
  props<{ threadId: string; attachment: ForumAttachment }>(),
);
export const forumThreadAttachmentCreateFailure = createAction(
  `${prefix} Thread Attachment Create Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadAttachmentDelete = createAction(
  `${prefix} Thread Attachment Delete`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    attachmentId: string;
  }>(),
);
export const forumThreadAttachmentDeleteSuccess = createAction(
  `${prefix} Thread Attachment Delete Success`,
  props<{ threadId: string; attachmentId: string }>(),
);
export const forumThreadAttachmentDeleteFailure = createAction(
  `${prefix} Thread Attachment Delete Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadReadsLoad = createAction(
  `${prefix} Thread Reads Load`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    openModal?: boolean;
  }>(),
);
export const forumThreadReadsLoadSuccess = createAction(
  `${prefix} Thread Reads Load Success`,
  props<{ threadId: string; reads: ForumThreadRead[]; openModal?: boolean }>(),
);
export const forumThreadReadsLoadFailure = createAction(
  `${prefix} Thread Reads Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadFavorite = createAction(
  `${prefix} Thread Favorite`,
  props<{ courseId: string; groupId: string; threadId: string }>(),
);
export const forumThreadFavoriteSuccess = createAction(
  `${prefix} Thread Favorite Success`,
  props<{ threadId: string }>(),
);
export const forumThreadFavoriteFailure = createAction(
  `${prefix} Thread Favorite Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadUnfavorite = createAction(
  `${prefix} Thread Unfavorite`,
  props<{ courseId: string; groupId: string; threadId: string }>(),
);
export const forumThreadUnfavoriteSuccess = createAction(
  `${prefix} Thread Unfavorite Success`,
  props<{ threadId: string }>(),
);
export const forumThreadUnfavoriteFailure = createAction(
  `${prefix} Thread Unfavorite Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadsLockAll = createAction(
  `${prefix} Threads Lock All`,
  props<{ courseId: string; groupId: string; threadId: string }>(),
);
export const forumThreadsLockAllSuccess = createAction(
  `${prefix} Threads Lock All Success`,
);
export const forumThreadsLockAllFailure = createAction(
  `${prefix} Threads Lock All Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadsUnlockAll = createAction(
  `${prefix} Threads Unlock All`,
  props<{ courseId: string; groupId: string; threadId: string }>(),
);
export const forumThreadsUnlockAllSuccess = createAction(
  `${prefix} Threads Unlock All Success`,
);
export const forumThreadsUnlockAllFailure = createAction(
  `${prefix} Threads Unlock All Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumReactionOptionsLoad = createAction(
  `${prefix} Reaction Options Load`,
  props<{ courseId: string; groupId: string }>(),
);
export const forumReactionOptionsLoadSuccess = createAction(
  `${prefix} Reaction Options Load Success`,
  props<{ options: ForumReactionOption[] }>(),
);
export const forumReactionOptionsLoadFailure = createAction(
  `${prefix} Reaction Options Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumMembersLoad = createAction(
  `${prefix} Members Load`,
  props<{ courseId: string; groupId: string }>(),
);
export const forumMembersLoadSuccess = createAction(
  `${prefix} Members Load Success`,
  props<{ members: ForumMember[] }>(),
);
export const forumMembersLoadFailure = createAction(
  `${prefix} Members Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumOverviewGroupsLoad = createAction(
  `${prefix} Overview Groups Load`,
  props<{
    page?: number;
    groupName?: string;
    courseName?: string;
    ordering?: "group_name" | "course_name";
  }>(),
);
export const forumOverviewGroupsLoadSuccess = createAction(
  `${prefix} Overview Groups Load Success`,
  props<{ groups: ForumOverviewGroup[]; next: number | null; page: number }>(),
);
export const forumOverviewGroupsLoadFailure = createAction(
  `${prefix} Overview Groups Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadReactionCreate = createAction(
  `${prefix} Thread Reaction Create`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    payload: ForumReactionActionPayload;
  }>(),
);
export const forumThreadReactionCreateSuccess = createAction(
  `${prefix} Thread Reaction Create Success`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    reaction: ForumThreadReaction;
  }>(),
);
export const forumThreadReactionCreateFailure = createAction(
  `${prefix} Thread Reaction Create Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadReactionDelete = createAction(
  `${prefix} Thread Reaction Delete`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    emoji: string;
  }>(),
);
export const forumThreadReactionDeleteSuccess = createAction(
  `${prefix} Thread Reaction Delete Success`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    emoji: string;
  }>(),
);
export const forumThreadReactionDeleteFailure = createAction(
  `${prefix} Thread Reaction Delete Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumThreadReactionChanged = createAction(
  `${prefix} Thread Reaction Changed`,
  props<{ threadId: string; reactionsSummary: ForumReactionSummary[] }>(),
);

export const forumThreadReactionDetailsLoad = createAction(
  `${prefix} Thread Reaction Details Load`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    emoji: string;
    currentUserId?: string | null;
    openModal?: boolean;
  }>(),
);
export const forumThreadReactionDetailsLoadSuccess = createAction(
  `${prefix} Thread Reaction Details Load Success`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    emoji: string;
    currentUserId?: string | null;
    details: ForumThreadReactionDetails;
    openModal?: boolean;
  }>(),
);
export const forumThreadReactionDetailsLoadFailure = createAction(
  `${prefix} Thread Reaction Details Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumPostReactionCreate = createAction(
  `${prefix} Post Reaction Create`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    postId: string;
    payload: ForumReactionActionPayload;
  }>(),
);
export const forumPostReactionCreateSuccess = createAction(
  `${prefix} Post Reaction Create Success`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    postId: string;
    emoji: string;
  }>(),
);
export const forumPostReactionCreateFailure = createAction(
  `${prefix} Post Reaction Create Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumPostReactionDelete = createAction(
  `${prefix} Post Reaction Delete`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    postId: string;
    emoji: string;
  }>(),
);
export const forumPostReactionDeleteSuccess = createAction(
  `${prefix} Post Reaction Delete Success`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    postId: string;
    emoji: string;
  }>(),
);
export const forumPostReactionDeleteFailure = createAction(
  `${prefix} Post Reaction Delete Failure`,
  props<{ error: HttpErrorResponse }>(),
);

export const forumPostReactionChanged = createAction(
  `${prefix} Post Reaction Changed`,
  props<{
    threadId: string;
    postId: string;
    reactionsSummary: ForumReactionSummary[];
  }>(),
);

export const forumPostReactionsLoad = createAction(
  `${prefix} Post Reactions Load`,
  props<{
    courseId: string;
    groupId: string;
    threadId: string;
    postId: string;
  }>(),
);
export const forumPostReactionsLoadSuccess = createAction(
  `${prefix} Post Reactions Load Success`,
  props<{ postId: string; reactions: ForumPostReaction[] }>(),
);
export const forumPostReactionsLoadFailure = createAction(
  `${prefix} Post Reactions Load Failure`,
  props<{ error: HttpErrorResponse }>(),
);
